import React, { useState, useEffect, useRef } from "react";
import { Add } from "@mui/icons-material";
import {
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  Tooltip,
  IconButton,
} from "@mui/material";
import { SingleSelectType } from "../../table/Filter";
import { useFormik } from "formik";
import * as yup from "yup";
import { startCase } from "lodash";
import {
  Log,
  getPaymentHostRelation,
  getPayoutStatic,
  payoutAction,
} from "../../../services/services";
import { DatePicker, LocalizationProvider } from "@mui/lab";
import AdapterMoment from "@mui/lab/AdapterMoment";
import { Info } from "@mui/icons-material";
import { styles } from "../../../utils/styles";
import FormDialog from "../../FormDialog";

const CycleTooltip = () => {
  const CustomTooltip = () => (
    <Box>
      <Typography>
        A - Tuesday and Friday <br />B - Monday, Wednesday and Friday <br />C -
        T+2 <br />D - Friday Only <br />E - Tuesday and Thursday <br />F - T+3{" "}
        <br />G - T+4 <br />H - T+5
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={<CustomTooltip />} placement="right-end">
      <IconButton sx={{ mt: 2, ml: -1 }}>
        <Info />
      </IconButton>
    </Tooltip>
  );
};

function CreatePayoutDialog() {
  const formRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [paymentRelation, setPaymentRelation] = useState([]);
  const [staticFields, setStaticFields] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({
    merchant: "",
    payment_host: "",
    annual_fee_start_date: new Date(),
  });

  useEffect(() => {
    getPaymentHostRelation(setPaymentRelation, setLoaded);
    getPayoutStatic(setStaticFields);
  }, []);

  //Dialog Stuff
  const [open, setOpen] = useState(false);
  const handleClickOpen = () => {
    Log.info("Click button [New Merchant Payout] - Show popup Add New Merchant Payout");
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  const validationSchema = yup.object({
    initialValues: {},
    email: yup
      .string("Enter your email")
      .email("Enter a valid email")
      .required("Email is required"),
    password: yup
      .string("Enter your password")
      .min(8, "Password should be of minimum 8 characters length")
      .required("Password is required"),
    merchant: yup.string().required(),
    payment_host: yup.string().required(),
    payment_cycle: yup.string().required(),
  });

  const formik = useFormik({
    initialValues: {
      merchant: "",
      payment_host: "",
      payment_cycle: "",
      annual_fee: "",
      annual_fee_period: "",
      minimum_payout_amount: "",
      annual_fee_start_date: "",
      fixed_amount: "",
      rolling_amount: "",
      account_number: "",
      bank: "",
      bank_address: "",
      bic_swift_code: "",
      beneficiary_name: "",
      beneficiary_address: "",
      beneficiary_bank_code: "",
      beneficiary_branch_code: "",
      iban: "",
      intermediary_bank: "",
      intermediary_bank_code: "",
      intermediary_name: "",
      bank_transfer_fee: "",
    },
    // validationSchema: validationSchema,
    validationSchema: yup.object().shape(
      {
        // merchant: yup.string().required("This field is required"),
        // payment_host: yup.string().required("This field is required"),
        // payment_cycle: yup.string().required("This field is required"),

        account_number: yup.string().required("This field is required"),
        bank: yup.string().required("This field is required"),
        bank_address: yup.string().required("This field is required"),
        bank_transfer_fee: yup.string().required("This field is required"),
        bic_swift_code: yup.string().when(["iban"], {
          is: (iban) => !iban || iban.length === 0,
          then: yup
            .string()
            .required(
              "At least one field is required (Iban or Bic swift code)"
            ),
        }),
        iban: yup.string().when(["bic_swift_code"], {
          is: (a) => !a || a.length === 0,
          then: yup
            .string()
            .required(
              "At least one field is required (Iban or Bic swift code)"
            ),
        }),
      },
      ["bic_swift_code", "iban"]
    ),
    onSubmit: (values, { setSubmitting }) => {
      let final = {
        ...values,
        ...selectedOptions,
        annual_fee_start_date: selectedOptions.annual_fee_start_date
          .toISOString()
          .slice(0, 10),
      };
      Log.info("Click button [Submit] - Add New Merchant Payout", final);
      payoutAction("ADD", null, null, final);
      handleClose();
      setSubmitting(false);
    },
  });

  const bank_details = [
    { name: "account_number", sx: ``, required: true },
    { name: "bank", sx: ``, required: true },
    { name: "bank_address", sx: { width: "50ch!important" }, required: true },
    { name: "bic_swift_code", sx: ``, required: true },
    { name: "iban", sx: ``, required: true },
    { name: "bank_transfer_fee", sx: ``, type: "number", required: true },
    { type: "divider" },
    { name: "beneficiary_name", sx: `` },
    { name: "beneficiary_bank_code", sx: `` },
    { name: "beneficiary_branch_code", sx: `` },
    { name: "beneficiary_address", sx: { width: "50ch!important" } },
    { type: "divider" },
    { name: "intermediary_bank", sx: `` },
    { name: "intermediary_bank_code", sx: `` },
    { name: "intermediary_name", sx: `` },
  ];

  const paymentHostOptions = () => {
    const options = [];
    if (selectedOptions.merchant !== "") {
      const found_id = paymentRelation.find(
        (e) => e.id === selectedOptions.merchant
      );
      if (found_id && found_id.terminal_merchant.length !== 0) {
        found_id.terminal_merchant[0].payment_mapping_terminal.forEach((host) =>
          options.push({
            name: host.payment_host.name,
            id: host.payment_host.id,
          })
        );
      }
    }
    return options;
  };

  return (
    <div>
      <Button variant="contained" onClick={handleClickOpen} startIcon={<Add />} sx={{ ...styles.customButton }}>
        New Merchant Payout
      </Button>
      <FormDialog
        open={open}
        onClose={handleClose}
        title="Add New Merchant Payout"
        handleSubmit={() => formRef.current?.requestSubmit()}
        loading={formik.isSubmitting}
      >
        <form ref={formRef} onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              "& .MuiFormControl-root": { m: 1, width: "25ch" },
            }}
          >
            <SingleSelectType
              id="true"
              options={paymentRelation.map((merchant) => ({
                name: merchant.name,
                id: merchant.id,
              }))}
              selected={selectedOptions}
              setSelected={setSelectedOptions}
              label="Merchant"
              param={"merchant"}
              required={true}
            />
            <SingleSelectType
              id="true"
              options={paymentHostOptions()}
              selected={selectedOptions}
              setSelected={setSelectedOptions}
              label="Payment Host"
              param={"payment_host"}
              required={true}
            />
            <SingleSelectType
              id="true"
              options={
                staticFields.cycle &&
                staticFields.cycle.map((cycle) => ({
                  name: cycle.cycle,
                  id: cycle.id,
                }))
              }
              selected={selectedOptions}
              setSelected={setSelectedOptions}
              label="Payment Cycle"
              param={"payment_cycle"}
              required
            />
            <CycleTooltip />
            <Divider sx={{ my: 1 }} />
            <TextField
              fullWidth
              id="annual_fee"
              name="annual_fee"
              label="Annual Fee"
              value={formik.values.annual_fee}
              type="number"
              onChange={formik.handleChange}
              error={
                formik.touched.annual_fee && Boolean(formik.errors.annual_fee)
              }
            />
            <SingleSelectType
              options={[
                { id: 1, annual_fee_period: "Monthly" },
                { id: 2, annual_fee_period: "Quarterly" },
                { id: 3, annual_fee_period: "Annually" },
              ].map((period) => period.annual_fee_period)}
              selected={selectedOptions}
              setSelected={setSelectedOptions}
              label="Annual Fee Period"
              param={"annual_fee_period"}
            />
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Annual fee start date"
                id="annual_fee_start_date"
                value={selectedOptions.annual_fee_start_date}
                onChange={(e) =>
                  setSelectedOptions({
                    ...selectedOptions,
                    annual_fee_start_date: e,
                  })
                }
                renderInput={(params) => <TextField {...params} />}
                required
              />
            </LocalizationProvider>

            <Divider sx={{ my: 1 }} />
            <TextField
              fullWidth
              id="fixed_amount"
              name="fixed_amount"
              label="One-time Fixed Charge"
              value={formik.values.fixed_amount}
              type="number"
              onChange={formik.handleChange}
              error={
                formik.touched.fixed_amount &&
                Boolean(formik.errors.fixed_amount)
              }
            />
            <TextField
              fullWidth
              id="minimum_payout_amount"
              name="minimum_payout_amount"
              label="Minimum Payout Amount"
              value={formik.values.minimum_payout_amount}
              type="number"
              onChange={formik.handleChange}
              error={
                formik.touched.minimum_payout_amount &&
                Boolean(formik.errors.minimum_payout_amount)
              }
            />

            <TextField
              fullWidth
              id="rolling_amount"
              name="rolling_amount"
              label="Rolling Amount"
              value={formik.values.rolling_amount}
              type="number"
              onChange={formik.handleChange}
              error={
                formik.touched.rolling_amount &&
                Boolean(formik.errors.rolling_amount)
              }
            />
            <Typography variant="h5" sx={{ my: 1 }}>
              Bank Details
            </Typography>
            {bank_details.map((field, index) => {
              return field.type !== "divider" ? (
                <TextField
                  fullWidth
                  id={field.name}
                  name={field.name}
                  label={startCase(field.name) + (field.required && "*")}
                  type={field.type ? field.type : "string"}
                  value={formik.values[field.name]}
                  onChange={formik.handleChange}
                  error={
                    formik.touched[field.name] &&
                    Boolean(formik.errors[field.name])
                  }
                  // required={field.required}
                  key={field.name}
                  sx={field.sx || {}}
                />
              ) : (
                <Divider key={index} sx={{ my: 1 }} />
              );
            })}
          </Box>
        </form>
      </FormDialog>
    </div>
  );
}

export default CreatePayoutDialog;
